
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import ModalTemporaryStudentData from '@/components/templates/manage/ModalTemporaryStudentData.vue'
import { GradeCode } from '@/const/GradeCode'
import { SelectItem } from '@/models/SelectItem'
import LoginIdUtils from '@/utils/LoginIdUtils'
import PasswordUtils from '@/utils/PasswordUtils'
import { BranchesApi } from '@/api/methods/manage/BranchesApi'
import { AcademyCoursesApi } from '@/api/methods/manage/AcademyCoursesApi'
import { AcademyClassesApi } from '@/api/methods/manage/AcademyClassesApi'
import { TemporaryStudentsApi } from '@/api/methods/manage/TemporaryStudentsApi'
import { ApiBadRequestAppError } from '@/models/AppErrorClasses'

/**
 * 仮生徒登録モーダル
 */
@Component({
  components: {
    ModalTemporaryStudentData,
  },
})
export default class ModalRegistTemporaryStudent extends Vue {
  /** 仮生徒登録モーダルのフォーマット */
  @Ref()
  modalTemporaryStudentData!: ModalTemporaryStudentData

  /** 生徒登録後の処理 */
  @Prop()
  private afterCreateStudent!: () => Promise<void>

  /** 学年選択の初期固定値:未選択状態 */
  private readonly OPTION_NOT_SELECTED_GRADE = { value: 'none', label: '学年を選択してください' }

  /** 教室選択の初期固定値:未選択状態 */
  private readonly OPTION_NOT_SELECTED_BRANCH = { value: 'none', label: '教室を選択してください' }

  /** コース選択の初期固定値:未選択状態 */
  private readonly OPTION_NOT_SELECTED_COURSE = { value: 'none', label: 'コースを選択してください' }

  /** クラス選択の初期固定値:未選択状態 */
  private readonly OPTION_NOT_SELECTED_CLASS = { value: 'none', label: 'クラスを選択してください' }

  /** 学年一覧 */
  private gradeList: SelectItem[] = []

  /** 学年選択の初期値 */
  private defaultGradeCode = ''

  /** 教室一覧 */
  private branchList: SelectItem[] = []

  /** 教室選択の初期値 */
  private defaultBranchCode = ''

  /** コース一覧 */
  private courseList: SelectItem[] = []

  /** コース選択の初期値 */
  private defaultCourseCode = ''

  /** クラス一覧 */
  private classList: SelectItem[] = []

  /** クラス選択の初期値 */
  private defaultClassCode = ''

  /** クラス選択のdisabled制御 */
  private disabledClassSelect = true

  /** ユーザのクッキー情報 */
  private userAcademy = this.$appCookies.authAcademy.get()

  /**
   * 確認ボタン押下時の処理
   */
  private async submit(): Promise<void> {
    this.$loading.start()

    let errorMessage = null

    // パスワードと確認用パスワードの一致チェック
    errorMessage = this.inputPasswordValidation()
    if (errorMessage) {
      alert(errorMessage)
      this.$loading.complete()
      return
    }

    // ログインIDが適切かのチェック
    errorMessage = LoginIdUtils.checkLoginIdEnabled(
      this.modalTemporaryStudentData.loginId,
      this.userAcademy.academyCode
    )
    if (errorMessage) {
      alert(errorMessage)
      this.$loading.complete()
      return
    }

    // パスワード指定が適切かのチェック
    errorMessage = PasswordUtils.checkPasswordEnabled(this.modalTemporaryStudentData.password)
    if (errorMessage) {
      alert(errorMessage)
      this.$loading.complete()
      return
    }

    // 任意のパラメータを設定
    let options = {}
    // 生徒識別子の指定があれば追加
    if (this.modalTemporaryStudentData.identifyTag) {
      Object.assign(options, { identifyTag: this.modalTemporaryStudentData.identifyTag })
    }

    try {
      // 生徒登録
      await TemporaryStudentsApi.createTemporaryStudent(
        this.modalTemporaryStudentData.nickname,
        this.modalTemporaryStudentData.loginId,
        this.modalTemporaryStudentData.password,
        this.modalTemporaryStudentData.gradeSelect.selectedValue(),
        Number(this.modalTemporaryStudentData.branchSelect.selectedValue()),
        Number(this.modalTemporaryStudentData.classSelect.selectedValue()),
        options
      )
    } catch (error) {
      if (error instanceof ApiBadRequestAppError) {
        alert(`
          入力値に不正があります
          ログインIDが使用済みである可能性があります
        `)
        this.$loading.complete()
        return
      }

      // 400エラー以外の場合は例外を投げ直し、共通処理に渡す
      throw error
    }

    // 生徒登録後の処理
    //   メソッド内で仮生徒一覧の再取得を行う
    await this.afterCreateStudent()

    this.$loading.complete()

    // モーダルを閉じる
    this.hide()
  }

  /**
   * 登録項目の入力検証(未入力、不一致など各検証項目をチェックする)
   *
   * @returns 検証項目に該当：エラーメッセージ 全ての検証をパス：null
   */
  private inputPasswordValidation(): string | null {
    // パスワードと確認用パスワードが一致しない
    if (this.modalTemporaryStudentData.password !== this.modalTemporaryStudentData.confirmPassword) {
      return 'パスワードと確認用パスワードが一致しません'
    }

    return null
  }

  /**
   * モーダルを開く
   */
  public async show(): Promise<void> {
    this.$loading.start()

    // 学年選択設定
    this.setGradeList()

    // 教室選択設定
    await this.setBranchList()

    // コース選択設定
    await this.setCourseList()

    // クラス選択はコース選択後に取得され得るため、未選択時の定数のみ設定
    this.classList = [this.OPTION_NOT_SELECTED_CLASS]
    // クラス選択の初期値を設定
    this.defaultClassCode = this.OPTION_NOT_SELECTED_CLASS.value
    // クラス選択をdisabled設定
    this.disabledClassSelect = true

    // モーダルを開く
    this.modalTemporaryStudentData.show()

    this.$loading.complete()
  }

  /**
   * 学年一覧の設定
   */
  private setGradeList(): void {
    // 学年プルダウン設定
    this.gradeList = GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })

    // 学年プルダウンの先頭に未選択状態の定数を設定
    this.gradeList.unshift(this.OPTION_NOT_SELECTED_GRADE)

    // 初期選択値を設定
    this.defaultGradeCode = this.OPTION_NOT_SELECTED_GRADE.value
  }

  /**
   * 教室一覧の設定
   */
  private async setBranchList(): Promise<void> {
    // 教室一覧取得
    const branches = await BranchesApi.getBranches()

    // 教室プルダウン設定
    this.branchList = branches.map((branch) => {
      return {
        // 教室IDをコードとして使用
        value: String(branch.id),
        label: branch.name,
      }
    })

    // 教室プルダウンの先頭に未選択状態の定数を設定
    this.branchList.unshift(this.OPTION_NOT_SELECTED_BRANCH)

    // 初期選択値を設定
    this.defaultBranchCode = this.OPTION_NOT_SELECTED_BRANCH.value
  }

  /**
   * コース選択の設定
   */
  private async setCourseList(): Promise<void> {
    // コース一覧取得
    const options = { academyId: this.userAcademy.academyId }
    const courses = await AcademyCoursesApi.getAll(options)

    // コース選択設定
    this.courseList = courses.map((course) => {
      return {
        // コースIDをコードとして使用
        value: String(course.id),
        label: course.name,
      }
    })

    // コース選択の先頭に未選択状態の定数を設定
    this.courseList.unshift(this.OPTION_NOT_SELECTED_COURSE)

    // 初期選択値を設定
    this.defaultCourseCode = this.OPTION_NOT_SELECTED_COURSE.value
  }

  /**
   * クラス選択の設定
   */
  private async setClassList(): Promise<void> {
    // コースが未選択の場合、処理を終了
    // 構成上発生しないが、予期せぬエラー回避の為記述
    if (this.modalTemporaryStudentData.courseSelect.selectedValue() === this.OPTION_NOT_SELECTED_COURSE.value) {
      return
    }

    // クラス一覧取得
    const classes = await AcademyClassesApi.getAll(Number(this.modalTemporaryStudentData.courseSelect.selectedValue()))

    // クラス選択設定
    this.classList = classes.map((academyClass) => {
      return {
        // クラスIDをコードとして使用
        value: String(academyClass.id),
        label: academyClass.name,
      }
    })

    // クラス選択の先頭に未選択状態の定数を設定
    this.classList.unshift(this.OPTION_NOT_SELECTED_CLASS)

    // 初期選択値を設定
    this.defaultClassCode = this.OPTION_NOT_SELECTED_CLASS.value
  }

  /**
   * コース選択変更時のイベント
   */
  private async changeCourseSelect(): Promise<void> {
    // コースが未選択の場合
    if (this.modalTemporaryStudentData.courseSelect.selectedValue() === this.OPTION_NOT_SELECTED_COURSE.value) {
      // クラス一覧をリセット
      this.classList = []
      // クラス選択に未設定状態の定数を設定
      this.classList.unshift(this.OPTION_NOT_SELECTED_CLASS)
      // 未選択状態に戻す
      this.modalTemporaryStudentData.classSelect.setCurrentValue(this.OPTION_NOT_SELECTED_CLASS.value)
      // クラス選択をdisabled
      this.disabledClassSelect = true
      return
    }

    this.$loading.start()

    // クラス選択設定
    await this.setClassList()

    // クラス選択のdisabledを解除
    this.disabledClassSelect = false

    this.$loading.complete()
  }

  /**
   * モーダルを閉じる
   */
  private hide(): void {
    this.modalTemporaryStudentData.hide()
  }
}
